import type { MealSuggestion, MealTemplate, RecipeStockIngredient } from '../domain/meals';
import type { RecipeShoppingListItem } from '../domain/model';
import { localizePair, type AppLocale } from './catalog';

type Pair = readonly [french: string, english: string];
const text = (locale: AppLocale, pair: Pair): string => localizePair(locale, pair[0], pair[1]);

const taxonomy: Record<string, Pair> = {
  'Bœuf': ['Bœuf', 'Beef'], Porc: ['Porc', 'Pork'], Volaille: ['Volaille', 'Poultry'], Agneau: ['Agneau', 'Lamb'], Charcuterie: ['Charcuterie', 'Cold cuts'],
  'Poisson blanc': ['Poisson blanc', 'White fish'], 'Poisson gras': ['Poisson gras', 'Oily fish'], Crustacés: ['Crustacés', 'Shellfish'], Coquillages: ['Coquillages', 'Molluscs'],
  Légumes: ['Légumes', 'Vegetables'], Herbes: ['Herbes', 'Herbs'], Soupe: ['Soupe', 'Soup'], Mélange: ['Mélange', 'Mix'],
  'Fruits rouges': ['Fruits rouges', 'Berries'], 'Fruits à noyau': ['Fruits à noyau', 'Stone fruit'], Agrumes: ['Agrumes', 'Citrus fruit'], Compote: ['Compote', 'Fruit compote'],
  'Plat maison': ['Plat maison', 'Homemade meal'], 'Plat préparé': ['Plat préparé', 'Prepared meal'], Sauce: ['Sauce', 'Sauce'], Bouillon: ['Bouillon', 'Stock'],
  Pain: ['Pain', 'Bread'], Viennoiserie: ['Viennoiserie', 'Viennoiserie'], Pâtisserie: ['Pâtisserie', 'Pastry'], Pâte: ['Pâte', 'Dough'],
  Glace: ['Glace', 'Ice cream'], Sorbet: ['Sorbet', 'Sorbet'], Gâteau: ['Gâteau', 'Cake'], Dessert: ['Dessert', 'Dessert'],
  Fromage: ['Fromage', 'Cheese'], Beurre: ['Beurre', 'Butter'], Crème: ['Crème', 'Cream'], Lait: ['Lait', 'Milk'], 'Autre aliment': ['Autre aliment', 'Other food'],
  Bourguignon: ['Bourguignon', 'Beef stew'], Steak: ['Steak', 'Steak'], Haché: ['Haché', 'Minced'], Rôti: ['Rôti', 'Roast'],
  Poulet: ['Poulet', 'Chicken'], Dinde: ['Dinde', 'Turkey'], Canard: ['Canard', 'Duck'], Cabillaud: ['Cabillaud', 'Cod'], Colin: ['Colin', 'Hake'], Merlu: ['Merlu', 'Hake'],
  Saumon: ['Saumon', 'Salmon'], Thon: ['Thon', 'Tuna'], Maquereau: ['Maquereau', 'Mackerel'], Courgette: ['Courgette', 'Courgette'], Carotte: ['Carotte', 'Carrot'], Haricot: ['Haricot', 'Bean'], Épinard: ['Épinard', 'Spinach'],
  Gratin: ['Gratin', 'Gratin'], Lasagnes: ['Lasagnes', 'Lasagne'], Ragoût: ['Ragoût', 'Stew'], Quiche: ['Quiche', 'Quiche'], Baguette: ['Baguette', 'Baguette'], 'Pain de mie': ['Pain de mie', 'Sliced bread'], 'Pain complet': ['Pain complet', 'Wholemeal bread'],
};

export const taxonomyLabel = (value: string, locale: AppLocale): string =>
  taxonomy[value] == null ? value : text(locale, taxonomy[value]);

const demoContent: Record<string, Pair> = {
  'Bœuf bourguignon': ['Bœuf bourguignon', 'Beef stew'],
  'Haricots verts du jardin': ['Haricots verts du jardin', 'Garden green beans'],
  'Filets de poisson': ['Filets de poisson', 'Fish fillets'],
  'Soupe de courge': ['Soupe de courge', 'Squash soup'],
  Fraises: ['Fraises', 'Strawberries'],
  'Donnée fictive de démonstration': ['Donnée fictive de démonstration', 'Fictional demo data'],
  'Vérifier les instructions de l’emballage': ['Vérifier les instructions de l’emballage', 'Check the packaging instructions'],
  'Foyer démonstration': ['Foyer démonstration', 'Demo household'],
  'Congélateur garage': ['Congélateur garage', 'Garage freezer'],
  'Tiroir du haut': ['Tiroir du haut', 'Top drawer'],
  'Tiroir du milieu': ['Tiroir du milieu', 'Middle drawer'],
  'Tiroir du bas': ['Tiroir du bas', 'Bottom drawer'],
  'Température inconnue — donnée fictive': ['Température inconnue — donnée fictive', 'Unknown temperature — fictional data'],
  'Démonstration fictive': ['Démonstration fictive', 'Fictional demo'],
  'Décision fictive pour illustrer une file de vérification partiellement traitée.': ['Décision fictive pour illustrer une file de vérification partiellement traitée.', 'Fictional decision illustrating a partly processed review queue.'],
};
export const demoContentLabel = (value: string, locale: AppLocale, demo: boolean): string =>
  !demo || demoContent[value] == null ? value : text(locale, demoContent[value]);

const templates: Record<string, { title: Pair; description: Pair }> = {
  soup: { title: ['Soupe ou velouté', 'Soup or creamy soup'], description: ['Une base souple pour utiliser légumes, herbes ou restes de plat.', 'A flexible base for using vegetables, herbs or meal leftovers.'] },
  gratin: { title: ['Gratin anti-gaspi', 'Waste-saving gratin'], description: ['Assembler une base congelée avec une liaison et un dessus croustillant.', 'Combine a frozen base with a binder and a crisp topping.'] },
  pan: { title: ['Poêlée express', 'Quick skillet'], description: ['Réchauffer une ou plusieurs portions et compléter avec un féculent.', 'Reheat one or more servings and add a starch.'] },
  pasta: { title: ['Pâtes ou riz en sauce', 'Pasta or rice with sauce'], description: ['Utiliser une sauce, une viande ou des légumes comme garniture.', 'Use a sauce, meat or vegetables as the topping.'] },
  tart: { title: ['Tarte ou chaussons', 'Tart or turnovers'], description: ['Transformer une petite quantité restante en garniture.', 'Turn a small leftover quantity into a filling.'] },
  dessert: { title: ['Dessert aux fruits', 'Fruit dessert'], description: ['Employer les fruits congelés en compote, crumble ou smoothie.', 'Use frozen fruit in a compote, crumble or smoothie.'] },
};

export const mealTemplateTitle = (template: Pick<MealTemplate, 'id' | 'title'>, locale: AppLocale): string =>
  templates[template.id] == null ? template.title : text(locale, templates[template.id]!.title);
export const mealTemplateDescription = (template: Pick<MealTemplate, 'id' | 'description'>, locale: AppLocale): string =>
  templates[template.id] == null ? template.description : text(locale, templates[template.id]!.description);

const ingredients: Record<string, Pair> = {
  'soup-vegetables': ['Légumes ou restes', 'Vegetables or leftovers'],
  'gratin-base': ['Garniture', 'Filling'], 'gratin-dairy': ['Crème ou fromage', 'Cream or cheese'],
  'pan-base': ['Base à poêler', 'Skillet base'], 'pan-protein': ['Viande ou poisson', 'Meat or fish'],
  'pasta-sauce': ['Sauce ou garniture', 'Sauce or topping'],
  'tart-filling': ['Garniture', 'Filling'], 'tart-dairy': ['Produit laitier', 'Dairy product'],
  'dessert-fruit': ['Fruits', 'Fruit'], 'dessert-dairy': ['Yaourt ou crème', 'Yogurt or cream'],
};
export const mealIngredientLabel = (ingredient: RecipeStockIngredient, locale: AppLocale): string =>
  ingredients[ingredient.id] == null ? ingredient.label : text(locale, ingredients[ingredient.id]!);

const pantry: Record<string, Pair> = {
  bouillon: ['bouillon', 'stock'], oignon: ['oignon', 'onion'], 'œuf ou crème': ['œuf ou crème', 'egg or cream'], 'fromage ou chapelure': ['fromage ou chapelure', 'cheese or breadcrumbs'],
  huile: ['huile', 'oil'], 'féculent au choix': ['féculent au choix', 'starch of your choice'], 'pâtes ou riz': ['pâtes ou riz', 'pasta or rice'], pâte: ['pâte', 'pastry'], œuf: ['œuf', 'egg'], 'sucre facultatif': ['sucre facultatif', 'optional sugar'],
};
export const mealPantryLabel = (value: string, locale: AppLocale): string =>
  pantry[value] == null ? value : text(locale, pantry[value]);

export const recipeShoppingItemLabel = (
  item: Pick<RecipeShoppingListItem, 'kind' | 'ingredientId' | 'label'>,
  locale: AppLocale,
): string => item.kind === 'pantry'
  ? mealPantryLabel(item.label, locale)
  : ingredients[item.ingredientId] == null
    ? item.label
    : text(locale, ingredients[item.ingredientId]!);

export const mealPresentIngredients = (suggestion: MealSuggestion, locale: AppLocale, demo = false): string[] =>
  suggestion.ingredientAvailability.filter((item) => item.present).map((item) =>
    `${mealIngredientLabel(item.ingredient, locale)} — ${item.containers.slice(0, 2).map((container) => demoContentLabel(container.name, locale, demo)).join(', ')}`,
  );

export const mealMissingIngredients = (
  suggestion: MealSuggestion,
  locale: AppLocale,
  quantityToCheck: string,
  missing: (quantity: number, unit: string) => string,
): string[] => {
  const stock = suggestion.ingredientAvailability.filter((item) => item.ingredient.required).flatMap((item) => {
    const label = mealIngredientLabel(item.ingredient, locale);
    if (!item.present) return [label];
    if (item.requiredQuantity != null && item.coveredQuantity == null) return [`${label} — ${quantityToCheck}`];
    if (item.missingQuantity != null && item.missingQuantity > 0 && item.ingredient.unit != null) {
      return [`${label} — ${missing(item.missingQuantity, item.ingredient.unit)}`];
    }
    return [];
  });
  return [...stock, ...suggestion.template.requiredPantryIngredients.map((item) => mealPantryLabel(item, locale))];
};
